/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable camelcase */
import React, {ReactElement, useEffect, useState} from 'react';
import {useTheme} from '@mui/system';
import {PulseLoader} from 'react-spinners';
import {useSnackbar} from 'notistack';

import './PeriodontalChartComponent.css';
import EmptyContent from '@components/empty-content';
import SnackBarConfig from '@components/snackbar/SnackbarConfig';
import {useAppContext} from '@context/AppContext';
import EncryptUtils from 'src/utils/EncryptUtils';
import {CircularProgressDiv} from '@modules/public/main/login/LoginStyles';
import FormConstants from '@shared/constants/FormConstants';
import {getErrorMessage} from '@shared/utils/ErrorUtils';
import {getPeriodontalChartData} from '@shared/services/service-handlers/PrivateService';
import {useModuleContext} from '@context/ModuleContext';
import AuthUtils from 'src/utils/AuthUtils';
// import PeriodontalChartTestData from './PeriodontalChartTestData.json';

interface PeriodontalChart {
  module: any;
  sub_module: any;
  subModuleUIData: any;
  rowId: any;
}

interface ToothSection {
  Name: string;
  ToothData: Record<string, string>;
}

interface TeethData {
  [key: string]: ToothSection[];
}

interface ChartData {
  upperTeeth: TeethData;
  lowerTeeth: TeethData;
}

const PeriodontalChartComponent = (props: PeriodontalChart): ReactElement => {
  //   for testing the Periodontal Chart use this json file
  // const testData: ChartData = PeriodontalChartTestData;
  const theme = useTheme();
  const {
    appState: {applicationName}
  } = useAppContext();

  const {
    moduleState: {isBH, isBHReason}
  } = useModuleContext();
  const {enqueueSnackbar} = useSnackbar();
  const [data, setData] = useState<ChartData | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const {module, sub_module, subModuleUIData, rowId} = props;

  // const sectionsData = testData ? Object.entries(testData) : [];
  const sectionsData = data ? Object.entries(data) : [];

  const init = async () => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const payload: any = {
          sql_data: subModuleUIData?.sql_data,
          record_id: rowId,
          sub_module_identifier: rowId,
          application_name: applicationName,
          isBH_selected: !isBHReason?.length && isBH == false ? true : isBH,
          isBH_reason: isBHReason,
          audit_sub_module_identifier: rowId,
          audit_identifier: AuthUtils.getAuditIdentifier(),
          audit_identifier_type: AuthUtils.getAuditIdentifierType(),
          audit_legacy_app: AuthUtils.getAuditLegacyApp()
        };
        const encryptRequestData = EncryptUtils.encryptedData(payload);
        const res = await getPeriodontalChartData(
          applicationName,
          module,
          sub_module,
          {
            tk: encryptRequestData
          }
        );
        setData(res?.data);
        setIsLoading(false);
      } catch (e) {
        const errMessage = getErrorMessage(e);
        enqueueSnackbar(errMessage, SnackBarConfig.getError());
        setIsLoading(false);
      }
    };
    fetchData();
  };

  useEffect(() => {
    init();
  }, []);

  const renderRows = (
    sectionKey: string,
    sections: ToothSection[],
    toothNumbers: any
  ) => {
    return sections.map((section, index) => (
      <React.Fragment key={section.Name}>
        <tr
          className={
            index === sections.length - 1 ? 'border-bottom-style' : ''
          }>
          {index === 0 && (
            <td rowSpan={sections.length} className="section-header">
              {sectionKey === 'EmptySurface' ? '' : sectionKey}
            </td>
          )}
          <td className="section-name">{section.Name}</td>
          {toothNumbers.map((tooth: any) => (
            <td key={tooth}>{section.ToothData[tooth] || ''}</td>
          ))}
        </tr>
      </React.Fragment>
    ));
  };

  const renderChart = (
    sectionName: string,
    teethData: TeethData,
    toothNumbers: any,
    toothPosition: 'top' | 'bottom',
    order: string[]
  ) => {
    // Check if any teethData is empty
    const hasData = Object.keys(teethData).some(
      key => teethData[key]?.length > 0
    );

    if (!hasData) {
      return (
        <EmptyContent
          isFilled
          isImage={false}
          title={sectionName}
          description={'No data available.'}
          sx={{
            py: 5,
            marginBottom: 2
          }}
        />
      );
    }

    return (
      <table>
        {toothPosition === 'top' && (
          <thead className="border-style">
            <tr>
              <th colSpan={2} className="border-style">
                Tooth #
              </th>
              {toothNumbers.map((tooth: any) => (
                <th key={tooth}>{tooth.replace('Tooth', '')}</th>
              ))}
            </tr>
          </thead>
        )}
        <tbody>
          {order.map(key => {
            const sections = teethData[key as keyof TeethData] || [];
            return renderRows(key, sections, toothNumbers);
          })}
        </tbody>
        {toothPosition === 'bottom' && (
          <tfoot className="border-style">
            <tr>
              <th colSpan={2} className="border-style">
                Tooth #
              </th>
              {toothNumbers.map((tooth: any) => (
                <th key={tooth}>{tooth.replace('Tooth', '')}</th>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    );
  };

  if (isLoading) {
    return (
      <CircularProgressDiv height="150px">
        <PulseLoader size={10} color={theme.palette.primary.main} />
      </CircularProgressDiv>
    );
  }

  if (!data || Object.keys(data)?.length === 0) {
    return (
      <EmptyContent
        isFilled
        title="No Data"
        sx={{
          py: 5,
          maxHeight: {md: 480},
          marginBottom: 2
        }}
      />
    );
  }

  return (
    <div className="bytepad-periodontal-chart">
      {sectionsData.map(([sectionName, teethData]) => {
        const isUpperTeeth =
          sectionName === FormConstants.PERIODONTAL_CHART.UPPER_TEETH;

        const order = Object.keys(teethData);
        const toothNumbers = order.length
          ? Object.keys(teethData[order[0]]?.[0]?.ToothData || {})
          : [];
        const surfaceName = isUpperTeeth ? 'Upper Teeth' : 'Lower Teeth';
        const toothPosition = isUpperTeeth ? 'bottom' : 'top';
        const sortedOrder = isUpperTeeth ? order : order.reverse();

        return renderChart(
          surfaceName,
          teethData,
          toothNumbers,
          toothPosition,
          sortedOrder
        );
      })}
    </div>
  );
};

export default PeriodontalChartComponent;
